'use client';

import {
  captureAnalyticsEvent,
  captureSnapshot,
  type CapturedSnapshot,
} from '@/reporting/earlyEvents';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import {
  canTrackPageLeave,
  commitPageLeaveAccepted,
  createPageLeavePropertiesOnce,
  resetPageLeaveState,
  type PageLeaveSnapshotSource,
  type PageLeaveState,
  type PageLeaveTrigger,
} from './pageLeaveTrackingState';

/** 同一document共享页面起点、once与卸载监听，StrictMode或重复mount不重置停留时间。 */
interface DocumentPageLeaveState {
  state: PageLeaveState;
  analyticsSnapshot: CapturedSnapshot | null;
  lastPathname: string | null;
  subscriptions: number;
  listener?: () => void;
}
/** 浏览器文档隔离，SSR不创建共享用户事件状态。 */
const documentStates = new WeakMap<Document, DocumentPageLeaveState>();

/** 仅在浏览器effect/事件回调内取得幂等状态，不在React渲染阶段修改共享状态。 */
function getDocumentPageLeaveState(): DocumentPageLeaveState {
  let state = documentStates.get(document);
  if (!state) {
    state = {
      state: { snapshot: null },
      analyticsSnapshot: null,
      lastPathname: null,
      subscriptions: 0,
    };
    documentStates.set(document, state);
  }
  return state;
}

/** 读取创建 PageLeave 快照所需的同一浏览器状态。 */
function getBrowserPageLeaveSource(): PageLeaveSnapshotSource | null {
  // SSR 或文档未就绪时不访问浏览器全局。
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  return {
    location: window.location,
    document,
    now: Date.now,
  };
}

/** 发送普通 PageLeave 业务事件，并保持旧 pathname/beforeunload once 语义。 */
export function useAnalyticsPageLeaveTracking(): void {
  const pathname = usePathname();

  /** 从进入页生成专用 PageLeave，并同步交给早期门面。 */
  const sendPageLeave = useCallback((trigger: PageLeaveTrigger, nextUrlPath?: string): void => {
    // App H5 与 SSR 环境不生成或发送 Web PageLeave。
    if (!canTrackPageLeave()) return;

    const source = getBrowserPageLeaveSource();

    // 浏览器数据源未就绪时安全跳过。
    if (!source) return;
    const lifecycle = getDocumentPageLeaveState();

    try {
      const properties = createPageLeavePropertiesOnce(
        lifecycle.state,
        trigger,
        source,
        nextUrlPath,
      );

      // 同一页面已被门面接收时不再捕获。
      if (!properties) return;

      const current = captureSnapshot();
      const entered = lifecycle.analyticsSnapshot;
      // 离开时冻结身份和时间，同时使用进入页的 page/pageViewId，避免变成下一页离开。
      const snapshot = entered
        ? { ...current, page: entered.page, pageViewId: entered.pageViewId }
        : current;
      const result = captureAnalyticsEvent({ kind: 'pageLeave', properties }, { snapshot });
      if (result.accepted) commitPageLeaveAccepted(lifecycle.state);
    } catch {
      // SDK 或浏览器读取异常不影响导航和卸载主链路。
    }
  }, []);

  // 首次只保存快照；仅 pathname 变化时发送上一页并重置下一页。
  useEffect(() => {
    if (!canTrackPageLeave()) return;

    const source = getBrowserPageLeaveSource();

    // 浏览器数据源未就绪时不修改状态。
    if (!source) return;
    const lifecycle = getDocumentPageLeaveState();

    // 首次挂载只记录进入页快照。
    if (!lifecycle.state.snapshot) {
      resetPageLeaveState(lifecycle.state, source);
      lifecycle.analyticsSnapshot = captureSnapshot();
      lifecycle.lastPathname = pathname;
      return;
    }

    // 兼容快照先由 beforeunload effect 初始化的执行顺序。
    if (lifecycle.lastPathname === null) {
      lifecycle.lastPathname = pathname;
      return;
    }

    // query-only 或同 pathname 重渲染不属于页面离开。
    if (lifecycle.lastPathname === pathname) return;

    sendPageLeave('pathname_change', pathname);

    const nextSource = getBrowserPageLeaveSource();

    // 为新 pathname 创建独立的一次性快照。
    if (nextSource) {
      resetPageLeaveState(lifecycle.state, nextSource);
      lifecycle.analyticsSnapshot = captureSnapshot();
    }

    lifecycle.lastPathname = pathname;
  }, [pathname, sendPageLeave]);

  // beforeunload 使用同一页面快照，cleanup 只移除监听而不发送。
  useEffect(() => {
    if (!canTrackPageLeave()) return;
    const lifecycle = getDocumentPageLeaveState();

    // 防御 route effect 尚未创建首屏快照的极端执行顺序。
    if (!lifecycle.state.snapshot) {
      const source = getBrowserPageLeaveSource();

      // 数据源有效时才创建首屏快照。
      if (source) {
        resetPageLeaveState(lifecycle.state, source);
        lifecycle.analyticsSnapshot = captureSnapshot();
      }
    }

    // 所有Hook共享一个实际listener；释放最后一个订阅仍保留当前页面once和起点。
    if (!lifecycle.listener) {
      /** 浏览器卸载前尝试发送当前页面离开。 */
      lifecycle.listener = () => sendPageLeave('beforeunload');
      window.addEventListener('beforeunload', lifecycle.listener);
    }
    lifecycle.subscriptions += 1;
    let disposed = false;

    /** 组件cleanup只释放订阅，document状态在真正新页面访问时才重置。 */
    return () => {
      if (disposed) return;
      disposed = true;
      lifecycle.subscriptions -= 1;
      if (lifecycle.subscriptions === 0 && lifecycle.listener) {
        window.removeEventListener('beforeunload', lifecycle.listener);
        lifecycle.listener = undefined;
      }
    };
  }, [sendPageLeave]);
}
